/**
 * Cart Page - Quotation Builder
 * Refactored version using modular components
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart, useAuth, formatINR, api, formatISTDdMmYyyyForFilename } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Trash2, FileDown, ArrowLeft, ShoppingCart, Plus, Zap, ChevronDown, Palette, Sparkles, Wrench, Pipette, TreePine, FolderPlus, Save, UserSearch, GitCompare, RefreshCw, FileStack, Users, FileText, Truck } from 'lucide-react';
import MarkdownInput from '../components/ui/MarkdownInput';
import { logger } from '../lib/logger';
import { joinWithHyphen } from '../lib/joinWithHyphen';

// Import refactored components
import { 
  CartItemsTable, 
  CartTotals, 
  SectionManager,
  ExtraChargeDialog,
  DuplicateReviewDialog,
  QuickAddVeneerDialog,
  QuickAddLaminateDialog,
  QuickAddHardwareDialog,
  QuickAddAdhesiveDialog,
  DEFAULT_CART_VENEER_QUICK_ADD,
  DEFAULT_CART_LAMINATE_QUICK_ADD,
  DEFAULT_CART_HARDWARE_QUICK_ADD,
  DEFAULT_CART_ADHESIVE_QUICK_ADD
} from '../components/cart';
import { ReplaceProductDialog } from '../components/cart/ReplaceProductDialog';

export default function CartPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editQuotationId = searchParams.get('edit');
  const isEditMode = !!editQuotationId;
  const isComparisonMode = searchParams.get('comparison') === 'true';
  const comparisonParentId = searchParams.get('parentId');
  
  // Track comparison status from loaded quotation
  const [quotationComparisonLabel, setQuotationComparisonLabel] = useState(null);
  const [quotationIsComparison, setQuotationIsComparison] = useState(false);
  // Safety-net banner: remember the original ref# and grand_total so the
  // user immediately notices if a number jumps unexpectedly after editing.
  const [originalQuotationRef, setOriginalQuotationRef] = useState(null);
  const [originalGrandTotal, setOriginalGrandTotal] = useState(null);
  
  const { items, removeItem, updateItem, clearCart, addQuickVeneer, replaceItem, loadQuotationForEdit, customer, setCustomer, subtotal } = useCart();
  const [overallDiscType, setOverallDiscType] = useState(searchParams.get('overallDiscType') || 'percent');
  const [overallDiscValue, setOverallDiscValue] = useState(parseFloat(searchParams.get('overallDiscValue')) || 0);
  const [subject, setSubject] = useState(decodeURIComponent(searchParams.get('subject') || ''));
  // Per-quotation T&C. A new quotation starts from the company default
  // (app_settings.terms); an edited one starts from its own saved
  // terms_override and falls back to the default only when it has none.
  // null = not decided yet. `defaultTerms` is kept to show "Custom terms".
  const [termsOverride, setTermsOverride] = useState(null);
  const [defaultTerms, setDefaultTerms] = useState(null);
  const [editTermsReady, setEditTermsReady] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Replace mode state (for comparison quotations)
  const [replaceItemId, setReplaceItemId] = useState(null);
  // Quick replace popup state — opened by clicking "Replace" in cart;
  // shows brands with the same thickness+size for one-click swap.
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [replaceTargetItem, setReplaceTargetItem] = useState(null);
  
  // Quick Add Dialog States
  const [showQuickVeneer, setShowQuickVeneer] = useState(false);
  const [quickVeneerData, setQuickVeneerData] = useState(DEFAULT_CART_VENEER_QUICK_ADD);
  const [showLaminateQuickAdd, setShowLaminateQuickAdd] = useState(false);
  const [laminateQuickAddData, setLaminateQuickAddData] = useState(DEFAULT_CART_LAMINATE_QUICK_ADD);
  const [showHardwareQuickAdd, setShowHardwareQuickAdd] = useState(false);
  const [hardwareQuickAddData, setHardwareQuickAddData] = useState(DEFAULT_CART_HARDWARE_QUICK_ADD);
  const [showAdhesiveQuickAdd, setShowAdhesiveQuickAdd] = useState(false);
  const [adhesiveQuickAddData, setAdhesiveQuickAddData] = useState(DEFAULT_CART_ADHESIVE_QUICK_ADD);
  
  // Section/Room management
  const [sections, setSections] = useState([]);
  const [itemSections, setItemSections] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);

  // Extra charges (Transportation, Labour, Installation, etc.) — each is
  // { label, amount } and is added between subtotal and grand total both
  // on screen and in the PDF.
  const [extraCharges, setExtraCharges] = useState([]);
  const [showExtraChargeDialog, setShowExtraChargeDialog] = useState(false);
  
  // PDF download type
  const [downloadingInternal, setDownloadingInternal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingComparison, setGeneratingComparison] = useState(false);

  // ── Duplicate-prompt state ────────────────────────────────────────
  // When the staff clicks Save (or Save & Download) and the backend
  // finds recent quotations that look like duplicates, we open a modal
  // BEFORE actually persisting. `pendingSaveRef` holds the closure
  // that finishes the save once the staff picks Replace / Keep both /
  // Cancel — using a ref (not state) avoids stale closure issues when
  // the user makes their choice asynchronously inside the dialog.
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupMatches, setDupMatches] = useState([]);
  const [dupPendingPayload, setDupPendingPayload] = useState(null);
  const pendingSaveRef = React.useRef(null);
  
  // Creator mode - select salesperson on whose behalf quotation is being created
  const [salespeople, setSalespeople] = useState([]);
  const [selectedSalesperson, setSelectedSalesperson] = useState(null);
  const [loadingSalespeople, setLoadingSalespeople] = useState(false);

  // Track if we've loaded this quotation to avoid refetching
  const [loadedQuotationId, setLoadedQuotationId] = useState(null);
  
  // Get current user role from localStorage
  const userRole = JSON.parse(localStorage.getItem('user') || '{}').role;
  const isCreatorRole = userRole === 'creator';
  const isAdminRole = userRole === 'admin';
  // Role permission quotations.create_on_behalf (by default admin, manager
  // and creator) can assign a quotation to a salesperson.
  const { can } = useAuth();
  const canSelectSalesperson = can('quotations.create_on_behalf');

  // Load salespeople list for creator role or admin (when editing quotations)
  useEffect(() => {
    if (canSelectSalesperson) {
      setLoadingSalespeople(true);
      api.getSalespeople()
        .then(res => {
          setSalespeople(res.data.salespeople || []);
        })
        .catch(err => {
          logger.error('Failed to load salespeople:', err);
          // Don't show error for admins, they might not need it
          if (isCreatorRole) {
            toast.error('Failed to load salespeople list');
          }
        })
        .finally(() => setLoadingSalespeople(false));
    }
  }, [canSelectSalesperson, isCreatorRole]);

  // Calculate overall discount and grand total (includes optional extra charges)
  const overallDiscount = overallDiscType === 'percent' ? subtotal * overallDiscValue / 100 : overallDiscValue;
  const extrasTotal = extraCharges.reduce((s, ec) => s + (parseFloat(ec.amount) || 0), 0);
  const grandTotal = subtotal - overallDiscount + extrasTotal;

  // Load the company default T&C once on mount; admins editing global terms
  // won't blow away a staff member's mid-quotation override.
  useEffect(() => {
    api.getSettings()
      .then(res => setDefaultTerms((res.data?.terms || []).slice()))
      .catch((err) => logger.warn('Failed to load settings:', err));
  }, []);

  // Start from the default terms once they are known: straight away for a
  // new quotation, after the quotation has loaded (and had no terms of its
  // own) when editing. The quotation's own terms win in either order.
  useEffect(() => {
    if (termsOverride !== null || defaultTerms === null) return;
    if (isEditMode && !editTermsReady) return;
    setTermsOverride(defaultTerms.slice());
  }, [termsOverride, defaultTerms, isEditMode, editTermsReady]);

  const termsList = termsOverride || [];
  const cleanTerms = (list) => (list || []).map(t => (t || '').trim()).filter(Boolean);
  const hasCustomTerms = termsOverride !== null && defaultTerms !== null
    && JSON.stringify(cleanTerms(termsOverride)) !== JSON.stringify(cleanTerms(defaultTerms));
  const referrerSummary = [
    customer.carpenter_name?.trim() && `Carp: ${customer.carpenter_name.trim()}`,
    customer.architect_name?.trim() && `Arch: ${customer.architect_name.trim()}`,
  ].filter(Boolean).join(' · ');

  // Load quotation data when in edit mode
  useEffect(() => {
    if (isEditMode && editQuotationId) {
      api.getQuotation(editQuotationId).then(res => {
        const quotation = res.data;
        // Capture original values for the safety-net banner
        setOriginalQuotationRef(quotation.quotation_number || quotation.ref_no || null);
        setOriginalGrandTotal(quotation.grand_total ?? null);
        
        // Track comparison status
        if (quotation.is_comparison) {
          setQuotationIsComparison(true);
          setQuotationComparisonLabel(quotation.comparison_label || (quotation.comparison_parent_id ? 'Option 2' : 'Option 1'));
        }
        
        if (items.length === 0 && loadedQuotationId !== editQuotationId) {
          const loadedSections = loadQuotationForEdit(quotation);
          if (loadedSections && loadedSections.length > 0) {
            setSections(loadedSections);
          }
        }
        
        if (quotation.customer && (!customer.name || customer.name.trim() === '')) {
          setCustomer(quotation.customer);
        }
        
        if (!subject && quotation.subject) {
          setSubject(quotation.subject);
        }
        if (overallDiscValue === 0 && quotation.overall_discount_value) {
          setOverallDiscValue(quotation.overall_discount_value);
          setOverallDiscType(quotation.overall_discount_type || 'percent');
        }
        // Load existing extra charges (Transportation, Labour, etc.)
        if (Array.isArray(quotation.extra_charges) && quotation.extra_charges.length > 0 && extraCharges.length === 0) {
          setExtraCharges(quotation.extra_charges.map(ec => ({ label: ec.label || '', amount: ec.amount || 0 })));
        }
        // The quotation's own T&C (edit and comparison edit). Without this a
        // re-save replaced custom terms with the company default.
        const ownTerms = Array.isArray(quotation.terms_override)
          ? quotation.terms_override.filter(t => (t || '').trim()) : [];
        if (ownTerms.length > 0) setTermsOverride(ownTerms.slice());
        setEditTermsReady(true);
        
        // Pre-fill the salesperson picker.
        //   • New on-behalf attribution flow: the salesperson IS the
        //     created_by, so we hydrate from created_by_* fields.
        //   • Legacy flow: created_for_* was stored separately, so we
        //     fall back to that.
        // Either way the picker shows who the quote is currently
        // assigned to, and the user can pick a different person to
        // reassign on save.
        if (quotation.created_for_user_id) {
          setSelectedSalesperson({
            id: quotation.created_for_user_id,
            name: quotation.created_for_name || '',
            staff_code: quotation.created_for_code || '',
            phone: quotation.created_for_phone || ''
          });
        } else if (quotation.created_by) {
          setSelectedSalesperson({
            id: quotation.created_by,
            name: quotation.created_by_name || '',
            staff_code: quotation.created_by_code || '',
            phone: quotation.created_by_phone || ''
          });
        }
        
        setLoadedQuotationId(editQuotationId);
      }).catch(err => {
        logger.error('Failed to load quotation for edit:', err);
      });
    }
    // Intentionally keyed on the edit target only — the other referenced
    // state (subject, discounts, items, customer) is read once to seed
    // defaults and must not re-trigger a quotation reload mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editQuotationId]);

  // Load sections from items when editing a quotation
  useEffect(() => {
    if (items.length > 0) {
      const itemsWithSections = items.filter(item => item.section && item.section.trim() !== '');
      if (itemsWithSections.length > 0) {
        const uniqueSections = [...new Set(itemsWithSections.map(item => item.section))];
        if (sections.length === 0 || (isEditMode && JSON.stringify(sections.sort()) !== JSON.stringify(uniqueSections.sort()))) {
          setSections(prevSections => {
            if (prevSections.length === 0) {
              return uniqueSections;
            }
            return prevSections;
          });
        }
        const newItemSections = {};
        items.forEach(item => {
          if (item.section && item.section.trim() !== '') {
            newItemSections[item.cart_id] = item.section;
          }
        });
        if (Object.keys(itemSections).length === 0 && Object.keys(newItemSections).length > 0) {
          setItemSections(newItemSections);
        }
      }
    }
  }, [items, isEditMode, itemSections, sections]);

  // Customer search
  useEffect(() => {
    if (customerSearch.length >= 2) {
      api.getCustomers(customerSearch).then(r => {
        setCustomerSuggestions(r.data);
        setShowSuggestions(r.data.length > 0);
      }).catch((err) => logger.warn('Customer search failed:', err));
    } else {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
    }
  }, [customerSearch]);

  const selectCustomer = (c) => {
    setCustomer({ name: c.name || '', mobile: c.mobile || '', address: c.address || '', gst_no: c.gst_no || '', project_name: c.project_name || '', delivery_location: c.delivery_location || '', carpenter_name: c.carpenter_name || '', architect_name: c.architect_name || '' });
    setShowSuggestions(false);
    setCustomerSearch('');
  };

  const handleAssignSection = (itemId, sectionName) => {
    setItemSections({ ...itemSections, [itemId]: sectionName });
  };

  // Build payload for saving quotation
  const buildPayload = () => ({
    items: items.map(i => ({
      product_id: i.product_id,
      description: i.description,
      brand: i.brand,
      brand_description: i.brand_description || '',  // For Plywood PDF header rows
      main_category: i.main_category || 'Others',
      size: i.size,
      thickness: i.thickness,
      quantity: i.quantity,
      unit: i.unit,
      rate: i.rate,
      rate_type: i.rate_type || '',
      discount_type: i.discount_type,
      discount_value: i.discount_value,
      cascading_discount: i.cascading_discount || '',
      cascading_discount_type: i.cascading_discount_type || 'percent',
      net_rate: i.net_rate,
      amount: i.amount,
      // Fix the "special_discount disappears after save" bug.
      // Both fields were being dropped by this whitelist builder → the
      // POST payload landed at the backend with no value → the field
      // was persisted as its schema default (0 / 'amount') → the next
      // reload showed the discount gone. Now included symmetrically
      // with `overall_discount_type` / `overall_discount_value` for
      // the Additional (whole-quote) discount.
      special_discount: Math.max(0, Number(i.special_discount) || 0),
      special_discount_type: ((i.special_discount_type || 'amount') + '').toLowerCase() === 'percent' ? 'percent' : 'amount',
      product_code: i.product_code || '',
      species: i.species || '',
      finish: i.finish || '',
      // Preserve PDF-relevant fields across save/edit cycles so the brand
      // header row + per-item description rebuild correctly on re-download.
      subcategory: i.subcategory || '',
      grade: i.grade || '',
      veneer_type: i.veneer_type || '',
      pack_size: i.pack_size || '',
      variant_size: i.variant_size || '',
      hardware_type: i.hardware_type || '',
      product_name: i.product_name || '',
      is_quick_add: i.is_quick_add || false,
      color: i.color,
      feature: i.feature,
      hardware_system_type: i.hardware_system_type,
      // Per-item brand visibility (Hardware/Screws/Patti). Default True so
      // existing items without this field keep their current behaviour.
      show_brand: i.show_brand !== false,
      nail_number: i.nail_number || '',
      section: itemSections[i.cart_id] || '',
      // Wooden Flooring box-billing metadata — drives the PDF sub-line
      // "1 box = X sq ft | N boxes = M sq ft" and ensures box × box_rate
      // billing survives save/edit round-trips.
      wooden_flooring: i.wooden_flooring || null,
    })),
    customer: customer,
    overall_discount_type: overallDiscType,
    overall_discount_value: overallDiscValue,
    extra_charges: extraCharges
      .filter(ec => (ec.label || '').trim())
      .map(ec => ({ label: ec.label.trim(), amount: parseFloat(ec.amount) || 0 })),
    subject: subject || `QUOTATION FOR ${customer.name.toUpperCase()}`,
    sections: sections.length > 0 ? sections : null,
    // Creator role: quotation created for a salesperson
    created_for_user_id: selectedSalesperson?.id || '',
    created_for_name: selectedSalesperson?.name || '',
    created_for_code: selectedSalesperson?.staff_code || '',
    created_for_phone: selectedSalesperson?.phone || '',
    // Per-quotation T&C override (only sent if staff actually edited it).
    terms_override: termsList.length > 0 ? cleanTerms(termsList) : null,
  });

  // ── Helpers for the at-save-time duplicate prompt ────────────────
  // `checkAndMaybePrompt` returns true if it opened the dialog (caller
  // must NOT proceed with the save — the dialog will call back). Returns
  // false when no duplicates were found and the caller should proceed.
  const checkAndMaybePrompt = async (payload, doSave) => {
    // Comparison options share the customer and (initially) the items of
    // their sibling options by design. The precheck would match Option 1
    // while saving Option 2 and offer "Replace", which supersedes Option 1
    // and breaks the comparison — so skip it for every comparison save.
    if (isComparisonMode || quotationIsComparison) return false;
    try {
      const { data } = await api.checkDuplicateQuotations(payload, editQuotationId || '');
      const matches = data?.matches || [];
      if (matches.length === 0) return false;
      // Stash the continuation. The dialog will call this with either
      // a `supersedeId` (Replace) or null (Keep both). Cancel = no-op.
      pendingSaveRef.current = doSave;
      setDupMatches(matches);
      setDupPendingPayload(payload);
      setDupDialogOpen(true);
      return true;
    } catch (err) {
      // Network error on the precheck must NEVER block a save — fall
      // through and let the save proceed as if no duplicates existed.
      // The legacy read-time dedupe still acts as a safety net.
      logger.warn('Duplicate-check failed, proceeding to save:', err);
      return false;
    }
  };

  // Called by the dialog's "Replace" button — performs the actual save
  // and then soft-deletes the picked old quotation.
  const handleDupReplace = async (oldId) => {
    setDupDialogOpen(false);
    const doSave = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!doSave) return;
    const newId = await doSave({ skipPrecheck: true });
    if (newId && oldId) {
      try {
        await api.supersedeQuotation(oldId, newId);
        toast.info(`Old quotation hidden from analytics.`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Save succeeded but failed to hide the old quotation');
      }
    }
  };

  // Called by the dialog's "Keep both" button — saves the new quote
  // and tags it with force_separate so the silent dedupe leaves it alone.
  const handleDupKeepBoth = async () => {
    setDupDialogOpen(false);
    const doSave = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!doSave) return;
    const newId = await doSave({ skipPrecheck: true });
    if (newId) {
      try {
        await api.markQuotationDistinct(newId);
      } catch (err) {
        // Non-fatal — staff already confirmed; if marking fails the
        // worst case is the read-time dedupe might merge them later.
        logger.warn('Failed to mark distinct:', err);
      }
    }
  };

  const handleDupCancel = () => {
    setDupDialogOpen(false);
    pendingSaveRef.current = null;
    // Reset the in-flight save spinners — the staff just cancelled.
    setSaving(false);
    setGenerating(false);
    setDownloadingInternal(false);
  };

  // Save changes without downloading PDF (Edit mode only)
  const handleSaveChanges = async () => {
    if (items.length === 0) { toast.error('Add products to the quotation first'); return; }
    if (!customer.name) { toast.error('Customer name is required'); return; }
    if (!customer.mobile || customer.mobile.replace(/\D/g, '').length < 10) {
      toast.error('Customer mobile number is required (at least 10 digits)');
      return;
    }
    if (!isEditMode) return;

    setSaving(true);
    const payload = buildPayload();

    // The actual save closure (also reused by Replace / Keep both paths).
    const doSave = async ({ skipPrecheck = false } = {}) => {
      try {
        const res = await api.updateQuotation(editQuotationId, payload);
        toast.success('Changes saved successfully!');
        if (customer.name) {
          api.saveCustomer(customer).catch((err) => logger.warn('Failed to save customer:', err));
        }
        const savedId = res.data.id;
        clearCart();
        navigate(`/quotations/${savedId}`);
        return savedId;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save changes');
        return null;
      } finally {
        setSaving(false);
      }
    };

    const prompted = await checkAndMaybePrompt(payload, doSave);
    if (!prompted) {
      await doSave();
    }
  };

  const handleGenerate = async (downloadInternal = false) => {
    if (items.length === 0) { toast.error('Add products to the quotation first'); return; }
    if (!customer.name) { toast.error('Customer name is required'); return; }
    if (!customer.mobile || customer.mobile.replace(/\D/g, '').length < 10) {
      toast.error('Customer mobile number is required (at least 10 digits)');
      return;
    }
    // Creator role: salesperson picker is OPTIONAL now.
    // If a creator doesn't pick anyone, the quotation is attributed to
    // themselves (their name appears in the PDF + History).
    // If they pick a salesperson, the quotation is attributed to that
    // person so it shows in their dashboard + admin analytics credit them.
    // Earlier we hard-blocked the submit when no salesperson was picked,
    // which prevented creators from logging their own walk-in customers.

    if (downloadInternal) {
      setDownloadingInternal(true);
    } else {
      setGenerating(true);
    }
    const payload = buildPayload();

    // Wrap the create/update + PDF-download in a closure so it can also
    // run after the duplicate-prompt is resolved.
    const doSave = async () => {
      try {
        let quotationId;
        if (isEditMode) {
          const res = await api.updateQuotation(editQuotationId, payload);
          quotationId = res.data.id;
          toast.success('Quotation updated successfully!');
        } else {
          const res = await api.createQuotation(payload);
          quotationId = res.data.id;
          toast.success('Quotation generated!');
        }

        if (customer.name) {
          api.saveCustomer(customer).catch((err) => logger.warn('Failed to save customer:', err));
        }

        const pdfRes = await api.downloadPdf(quotationId, downloadInternal);
        const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const suffix = downloadInternal ? '_INTERNAL' : '';
        a.download = `Quotation${suffix}_${customer.name.replace(/\s+/g, '_')}_${formatISTDdMmYyyyForFilename()}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);

        clearCart();
        navigate(`/quotations/${quotationId}`);
        return quotationId;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to generate quotation');
        return null;
      } finally {
        setGenerating(false);
        setDownloadingInternal(false);
      }
    };

    const prompted = await checkAndMaybePrompt(payload, doSave);
    if (!prompted) {
      await doSave();
    }
  };

  // Explicit "Save Quotation" for the NEW-flow (Cart with no
  // ?edit=). Creates the record via the SAME createQuotation call the
  // Customer/Internal PDF buttons already use, but skips the PDF
  // download. After a successful save the URL is rewritten to
  // ?edit={newId} so isEditMode flips to true — this means any
  // subsequent PDF-button click enters the updateQuotation branch in
  // handleGenerate and reuses the freshly-saved record instead of
  // POSTing a second one. Prevents the "click Save then click PDF →
  // two Mongo docs" duplication class this app has been burned by
  // before.
  const handleSaveQuotation = async () => {
    if (isEditMode) return;              // Edit mode already has its own Save
    if (items.length === 0) { toast.error('Add products to the quotation first'); return; }
    if (!customer.name) { toast.error('Customer name is required'); return; }
    if (!customer.mobile || customer.mobile.replace(/\D/g, '').length < 10) {
      toast.error('Customer mobile number is required (at least 10 digits)');
      return;
    }

    setSaving(true);
    const payload = buildPayload();

    const doSave = async () => {
      try {
        const res = await api.createQuotation(payload);
        const newId = res.data.id;
        if (customer.name) {
          api.saveCustomer(customer).catch((err) => logger.warn('Failed to save customer:', err));
        }
        // Flip URL to edit mode WITHOUT clearing the cart. This makes
        // the follow-up PDF click hit updateQuotation, not create.
        const next = new URLSearchParams(searchParams);
        next.set('edit', newId);
        setSearchParams(next, { replace: true });
        toast.success('Quotation saved — click Customer/Internal PDF to download');
        return newId;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save quotation');
        return null;
      } finally {
        setSaving(false);
      }
    };

    const prompted = await checkAndMaybePrompt(payload, doSave);
    if (!prompted) {
      await doSave();
    }
  };

  // Quick Add Handlers
  const handleQuickAddVeneer = () => {
    addQuickVeneer({
      species: quickVeneerData.species,
      product_code: quickVeneerData.product_code,
      size: quickVeneerData.size,
      rate: parseFloat(quickVeneerData.rate),
      quantity: parseFloat(quickVeneerData.quantity) || 1,
      // `uom` controls the rate × area expansion in addQuickVeneer:
      // 'sqft' → rate × sheet sqft × qty; 'sheet'/'piece' → rate × qty.
      uom: quickVeneerData.uom || 'sqft',
      unit: quickVeneerData.unit,
      show_brand: quickVeneerData.show_brand !== false,
    });
    
    toast.success(`Added ${quickVeneerData.species} veneer to quotation`);
    setShowQuickVeneer(false);
    setQuickVeneerData(DEFAULT_CART_VENEER_QUICK_ADD);
  };

  const handleLaminateQuickAdd = () => {
    const { brand, product_code, description, thickness, rate, size, category, quantity, uom, show_brand } = laminateQuickAddData;
    const baseRate = parseFloat(rate) || 0;
    const qty = parseFloat(quantity) || 1;
    // Build the cart-line label from non-empty parts ONLY so a blank
    // description never produces " - GL-1010 - 2MM" with a stray leading
    // hyphen. Order is fixed: description → code → thickness.
    // Brand is prepended only when the user explicitly ticked
    // "Brand on PDF" — otherwise we keep it out of the line entirely
    // (it still lives on the item.brand field for internal PDF).
    const composedName = joinWithHyphen(description, product_code, thickness);
    const species = (show_brand !== false)
      ? joinWithHyphen(brand, composedName)
      : composedName;
    addQuickVeneer({
      species,
      product_code: product_code.toUpperCase(),
      size: size,
      rate: baseRate,
      quantity: qty,
      // Map UOM → unit label shown in cart
      unit: (uom === 'sqft') ? 'NOS' : (uom === 'piece' ? 'NOS' : 'SHEET'),
      uom: uom || 'sheet',
      main_category: category,
      brand,
      // Persist description + thickness fields separately so the PDF
      // renderer can rebuild the line canonically (and the Edit dialog
      // can show them as separate inputs).
      finish: description || '',       // legacy field name kept for back-compat with PDF formatter
      description_text: description || '',
      thickness: thickness || '',
      show_brand: show_brand !== false,
    });
    toast.success(`Added ${product_code} ${category.slice(0,-1)} to quotation`);
    setShowLaminateQuickAdd(false);
    setLaminateQuickAddData(DEFAULT_CART_LAMINATE_QUICK_ADD);
  };

  const handleHardwareQuickAdd = () => {
    const { brand, product_name, product_code, specs, size, rate, rate_type, quantity, show_brand } = hardwareQuickAddData;
    const baseRate = parseFloat(rate) || 0;
    const qty = parseFloat(quantity) || 1;
    // Brand prepended only when the toggle is on. `joinWithHyphen` drops
    // empty parts so a missing product_name doesn't yield "- specs".
    const species = (show_brand !== false)
      ? joinWithHyphen(brand, product_name, specs)
      : joinWithHyphen(product_name, specs);
    addQuickVeneer({
      species,
      product_code: product_code || '',
      // Prefer the explicit size input; fall back to specs (legacy behaviour).
      size: (size || '').trim() || specs || '',
      rate: baseRate,
      quantity: qty,
      unit: rate_type.replace('per ', '').toUpperCase(),
      main_category: 'Hardware',
      brand,
      show_brand: show_brand !== false,
    });
    toast.success(`Added ${product_name} to quotation`);
    setShowHardwareQuickAdd(false);
    setHardwareQuickAddData(DEFAULT_CART_HARDWARE_QUICK_ADD);
  };

  const handleAdhesiveQuickAdd = () => {
    const { brand, product_name, product_code, weight, size, rate, quantity, show_brand } = adhesiveQuickAddData;
    const baseRate = parseFloat(rate) || 0;
    const qty = parseFloat(quantity) || 1;
    const species = (show_brand !== false)
      ? joinWithHyphen(brand, product_name)
      : (product_name || '');
    addQuickVeneer({
      species,
      product_code: product_code || '',
      // Prefer explicit size; fall back to weight as the size column.
      size: (size || '').trim() || weight,
      rate: baseRate,
      quantity: qty,
      unit: weight.includes('LTR') ? 'LTR' : 'KG',
      main_category: 'Adhesives',
      brand,
      show_brand: show_brand !== false,
    });
    toast.success(`Added ${brand} ${product_name} ${weight} to quotation`);
    setShowAdhesiveQuickAdd(false);
    setAdhesiveQuickAddData(DEFAULT_CART_ADHESIVE_QUICK_ADD);
  };

  // Handle comparison PDF generation
  const handleGenerateComparisonPdf = async () => {
    if (items.length === 0) { toast.error('Add products to the quotation first'); return; }
    if (!customer.name) { toast.error('Customer name is required'); return; }
    if (!customer.mobile || customer.mobile.replace(/\D/g, '').length < 10) {
      toast.error('Customer mobile number is required (at least 10 digits)');
      return;
    }

    setGeneratingComparison(true);
    try {
      // First save the quotation
      await api.updateQuotation(editQuotationId, buildPayload());
      
      if (customer.name) {
        api.saveCustomer(customer).catch((err) => logger.warn('Failed to save customer:', err));
      }
      
      // Download comparison PDF
      const pdfRes = await api.downloadComparisonPdf(editQuotationId, false);
      const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Comparison_Quotation_${customer.name.replace(/\s+/g, '_')}_${formatISTDdMmYyyyForFilename()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Comparison PDF downloaded!');
      clearCart();
      navigate(`/quotations/${editQuotationId}`);
    } catch (err) {
      // When responseType='blob', errors come back as a Blob (not JSON), so
      // err.response.data.detail is undefined. Decode the blob to surface the
      // real backend message instead of a generic toast.
      let detail = '';
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === 'function') {
          const text = await blob.text();
          try { detail = JSON.parse(text).detail || text; } catch (_) { detail = text; }
        }
      } catch (_) { /* ignore */ }
      detail = detail || err.response?.data?.detail || err.message || 'Unknown error';
      logger.error('Comparison PDF failed', err.response?.status, detail);
      toast.error(`Comparison PDF failed: ${String(detail).slice(0, 200)}`);
    } finally {
      setGeneratingComparison(false);
    }
  };

  // Open the quick-replace dialog (shows same size+thickness candidates).
  // Falls back to the long ProductsPage flow if the user clicks "Browse all".
  const handleReplaceProduct = (cartId, _category) => {
    const item = items.find(i => i.cart_id === cartId);
    if (!item) return;
    setReplaceTargetItem(item);
    setShowReplaceDialog(true);
  };

  // Fallback: navigate to the full ProductsPage picker (legacy long flow).
  const navigateToFullPicker = () => {
    const item = replaceTargetItem;
    if (!item) return;
    sessionStorage.setItem('replaceCartId', item.cart_id);
    sessionStorage.setItem('replaceCategory', item.main_category || '');
    sessionStorage.setItem('replaceEditId', editQuotationId || '');
    sessionStorage.setItem('replaceIsComparison', isComparisonMode ? 'true' : quotationIsComparison ? 'true' : 'false');
    sessionStorage.setItem('replaceParentId', comparisonParentId || '');
    sessionStorage.setItem('replaceSubject', subject || '');
    navigate(`/products?replace=true&category=${encodeURIComponent(item.main_category || '')}`);
  };

  // Apply the picked product from the quick-replace dialog.
  const handleQuickReplace = (product, config) => {
    if (!replaceTargetItem) return;
    replaceItem(replaceTargetItem.cart_id, product, config);
    toast.success(`Replaced with ${product.brand_name}${product.subcategory ? ' ' + product.subcategory : ''}`);
    setReplaceTargetItem(null);
  };

  return (
    <div data-testid="cart-page">
      {/* Comparison Mode Banner (for new Option 2 creation) */}
      {isComparisonMode && (
        <div className="bg-heda-gold-tint border border-heda-gold/30 rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="comparison-mode-banner">
          <GitCompare className="w-5 h-5 text-heda-gold" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-heda-ink">Option 2 - Comparison Mode</p>
            <p className="text-xs text-heda-mute">Modify products to create your comparison. Click "Replace" on any item to swap it with a different brand.</p>
          </div>
          <Badge className="rounded-full bg-heda-gold text-white">Option 2</Badge>
        </div>
      )}
      
      {/* Editing Comparison Quotation Banner (when editing existing Option 1 or Option 2) */}
      {!isComparisonMode && quotationIsComparison && (
        <div className="bg-heda-olive-tint border border-heda-olive/30 rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="edit-comparison-banner">
          <GitCompare className="w-5 h-5 text-heda-olive-dark" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-heda-olive-dark">Editing {quotationComparisonLabel} of Comparison Quotation</p>
            <p className="text-xs text-heda-mute">This is part of a comparison quotation. Use "Comparison PDF" button to download the combined PDF.</p>
          </div>
          <Badge className="rounded-full bg-heda-ink text-white">{quotationComparisonLabel}</Badge>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')} className="rounded-xl h-8 px-2 sm:px-3" data-testid="back-to-products-btn">
          <ArrowLeft className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Products</span>
        </Button>
        <Separator orientation="vertical" className="h-5 hidden sm:block" />
        <h1 className="font-display text-[20px] font-bold text-heda-ink">
          {isComparisonMode ? 'Comparison Quotation - Option 2' : 
           quotationIsComparison ? `Edit ${quotationComparisonLabel}` :
           isEditMode ? 'Edit Quotation' : 'Quotation Builder'}
        </h1>
        <span className="text-xs sm:text-sm text-heda-sub">({items.length})</span>
        {isEditMode && (
          <span className="text-[10px] sm:text-xs bg-heda-olive-tint text-heda-olive-dark px-2 py-0.5 rounded-full font-medium">Editing</span>
        )}
      </div>

      {/* SAFETY NET BANNER — only shown in edit mode, only on screen (never in PDF) */}
      {isEditMode && originalQuotationRef && (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-heda-gold/40 bg-heda-gold-tint px-3 py-2"
          data-testid="edit-safety-net-banner"
        >
          <div className="text-xs text-heda-sub">
            <span className="font-semibold text-heda-ink">Editing {originalQuotationRef}</span>
            {originalGrandTotal != null && (
              <>
                <span className="mx-2 text-heda-mute">·</span>
                <span>Original total: <span className="font-mono-data font-semibold text-heda-ink">{formatINR(originalGrandTotal)}</span></span>
                {Math.abs((originalGrandTotal || 0) - grandTotal) > 0.5 && (
                  <>
                    <span className="mx-2 text-heda-mute">→</span>
                    <span>
                      New: <span className="font-mono-data font-semibold text-heda-olive-dark">{formatINR(grandTotal)}</span>
                      <span className={`ml-1 font-semibold ${(grandTotal - (originalGrandTotal || 0)) >= 0 ? 'text-heda-olive-dark' : 'text-red-700'}`}>
                        ({(grandTotal - (originalGrandTotal || 0)) >= 0 ? '+' : ''}{formatINR(grandTotal - (originalGrandTotal || 0))})
                      </span>
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          <span className="text-[10px] text-heda-mute italic">Banner shown on screen only — not in PDF</span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Cart Items — min-w-0 lets this column shrink below the items
            table's natural width (the table scrolls inside its own card)
            so the fixed-width customer column stays on screen. */}
        <div className="flex-1 min-w-0">
          {items.length === 0 ? (
            <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid="empty-cart">
              <CardContent className="flex flex-col items-center py-16">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
                  <ShoppingCart className="h-7 w-7 text-heda-olive" />
                </div>
                <p className="mb-4 font-display font-bold text-heda-ink">No items in quotation</p>
                <Button onClick={() => navigate('/products')} className="rounded-xl bg-heda-ink hover:bg-black text-white" data-testid="add-products-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Products
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <CartItemsTable 
                items={items}
                sections={sections}
                itemSections={itemSections}
                updateItem={updateItem}
                removeItem={removeItem}
                handleAssignSection={handleAssignSection}
                isComparisonMode={isComparisonMode || quotationIsComparison}
                onReplaceProduct={handleReplaceProduct}
              />
              <CartTotals 
                subtotal={subtotal}
                overallDiscType={overallDiscType}
                setOverallDiscType={setOverallDiscType}
                overallDiscValue={overallDiscValue}
                setOverallDiscValue={setOverallDiscValue}
                extraCharges={extraCharges}
                setExtraCharges={setExtraCharges}
              />
            </>
          )}

          {/* Action buttons */}
          {items.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              <Button variant="outline" onClick={() => navigate('/products')} className="rounded-xl border-heda-line text-heda-sub h-8 text-xs sm:text-sm" data-testid="add-more-btn">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> <span className="hidden sm:inline">Add</span> More
              </Button>
              
              {/* Quick Add Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold h-8 text-xs sm:text-sm" data-testid="quick-add-dropdown-btn">
                    <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Quick Add <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem onClick={() => setShowLaminateQuickAdd(true)} className="cursor-pointer text-sm">
                    <Palette className="w-4 h-4 mr-2 text-sky-700" /> Laminate
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Highlighters' })); setShowLaminateQuickAdd(true); }} className="cursor-pointer text-sm">
                    <Sparkles className="w-4 h-4 mr-2 text-heda-gold" /> Highlighter
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowQuickVeneer(true)} className="cursor-pointer text-sm">
                    <TreePine className="w-4 h-4 mr-2 text-amber-700" /> Veneer
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowHardwareQuickAdd(true)} className="cursor-pointer text-sm">
                    <Wrench className="w-4 h-4 mr-2 text-heda-sub" /> Hardware
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowAdhesiveQuickAdd(true)} className="cursor-pointer text-sm">
                    <Pipette className="w-4 h-4 mr-2 text-heda-gold" /> Adhesive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Add Section */}
              <Button variant="outline" onClick={() => setShowAddSection(true)} className="rounded-xl border-heda-line text-heda-sub hover:bg-heda-cream/60 h-8 text-xs sm:text-sm" data-testid="add-section-btn">
                <FolderPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Add </span>Section
              </Button>

              {/* Extra Charge - opens a dialog to add Transportation /
                  Labour / Installation / custom charges. Only shows on the
                  PDF when staff explicitly adds something. */}
              <Button
                variant="outline"
                onClick={() => setShowExtraChargeDialog(true)}
                className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold h-8 text-xs sm:text-sm"
                data-testid="add-extra-charge-btn"
                title="Add Transportation, Labour, or custom charges"
              >
                <Truck className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Extra </span>Charge
              </Button>
              
              <Button variant="outline" onClick={clearCart} className="rounded-xl border-red-200 text-red-700 hover:bg-red-50 h-8 text-xs sm:text-sm ml-auto" data-testid="clear-cart-btn">
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> <span className="hidden sm:inline">Clear</span> All
              </Button>
            </div>
          )}
          
          {/* Sections Manager */}
          <SectionManager 
            sections={sections}
            setSections={setSections}
            setItemSections={setItemSections}
            showAddSection={showAddSection}
            setShowAddSection={setShowAddSection}
          />

          {/* Extra Charge composer dialog — opens from the action bar
              "Extra Charge" button. Submitted charges land in the totals
              block above (and on the PDF). */}
          <ExtraChargeDialog
            open={showExtraChargeDialog}
            onOpenChange={setShowExtraChargeDialog}
            onAdd={(charge) => setExtraCharges([...extraCharges, charge])}
          />
        </div>

        {/* Customer Details & Generate */}
        <div className="w-full lg:w-96 lg:shrink-0">
          {/* Desktop: the card sticks under the header and is never taller
              than the screen; the sections scroll inside it and the action
              buttons stay in view at its foot. Phones and tablets: one
              column, normal page flow (buttons at the end of the card). */}
          <Card className="rounded-2xl bg-white shadow-none border-heda-line lg:sticky lg:top-[135px] lg:flex lg:flex-col lg:max-h-[calc(100vh-150px)]" data-testid="customer-details-card">
            <CardContent className="pt-4 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
              <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:-mx-2 lg:px-2" data-testid="customer-details-scroll">
              <Accordion
                type="multiple"
                defaultValue={['customer']}
                className="w-full"
              >
                {/* ─── Customer Details ─── */}
                <AccordionItem value="customer" className="border-heda-line">
                  <AccordionTrigger
                    className="py-3 text-sm font-bold uppercase tracking-wide text-heda-ink hover:no-underline"
                    data-testid="accordion-customer-trigger"
                  >
                    Customer Details
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div className="space-y-3">
                      {/* Customer search */}
                      <div className="relative">
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Search Saved Customers</Label>
                        <div className="relative mt-1">
                          <UserSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-heda-mute" />
                          <Input
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            placeholder="Type name or mobile..."
                            className="rounded-xl pl-8 bg-heda-cream/50 border-heda-line text-sm"
                            data-testid="customer-search-input"
                          />
                        </div>
                        {showSuggestions && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-heda-line rounded-xl shadow-lg max-h-40 overflow-y-auto" data-testid="customer-suggestions">
                            {customerSuggestions.map(c => (
                              <button key={c.id} onClick={() => selectCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-heda-cream/60 border-b border-heda-line last:border-0">
                                <span className="font-medium">{c.name}</span>
                                {c.mobile && <span className="text-heda-mute text-xs ml-2">{c.mobile}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <Separator className="bg-heda-line" />

                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Subject / Title</Label>
                        <Input
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          placeholder="Enter title"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="subject-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Customer Name *</Label>
                        <Input
                          value={customer.name}
                          onChange={(e) => setCustomer(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter customer name"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-name-input"
                          required
                        />
                      </div>

                      {/* Salesperson picker — visible to admin / manager / creator.
                          Optional when creating a fresh quote; shows reassignment
                          capability when editing an existing one. */}
                      {canSelectSalesperson && (
                        <div className="bg-heda-gold-tint border border-heda-gold/30 rounded-xl p-3">
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                            <Users className="w-3 h-3" /> {isEditMode ? 'Assigned To (change to reassign)' : 'Quotation For (Optional — defaults to you)'}
                          </Label>
                          <Select
                            value={selectedSalesperson?.id || ''}
                            onValueChange={(id) => {
                              if (id === '_clear_') {
                                setSelectedSalesperson(null);
                              } else {
                                const person = salespeople.find(s => s.id === id);
                                setSelectedSalesperson(person || null);
                              }
                            }}
                          >
                            <SelectTrigger className="rounded-xl mt-1 bg-white border-heda-gold/30 text-sm" data-testid="salesperson-select">
                              <SelectValue placeholder={loadingSalespeople ? "Loading..." : "Select Salesperson"} />
                            </SelectTrigger>
                            <SelectContent>
                              {isAdminRole && (
                                <SelectItem value="_clear_">
                                  <span className="text-heda-mute">-- No salesperson (Self) --</span>
                                </SelectItem>
                              )}
                              {salespeople.map(person => (
                                <SelectItem key={person.id} value={person.id}>
                                  <div className="flex items-center gap-2">
                                    {person.staff_code && (
                                      <span className="font-mono text-xs bg-heda-olive-tint px-1.5 rounded-lg">{person.staff_code}</span>
                                    )}
                                    <span>{person.name}</span>
                                    <span className="text-xs text-heda-mute capitalize">({person.role})</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedSalesperson && (
                            <p className="text-[10px] text-heda-mute mt-1">
                              Quotation will be attributed to {selectedSalesperson.name} ({selectedSalesperson.staff_code || 'No code'})
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Mobile Number *</Label>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          value={customer.mobile}
                          onChange={(e) => setCustomer(prev => ({ ...prev, mobile: e.target.value.replace(/[^0-9]/g, '') }))}
                          placeholder="Enter mobile number"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-mobile-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Address</Label>
                        <Input
                          value={customer.address}
                          onChange={(e) => setCustomer(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="Enter address"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-address-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">GST No (Optional)</Label>
                        <Input
                          value={customer.gst_no}
                          onChange={(e) => setCustomer(prev => ({ ...prev, gst_no: e.target.value }))}
                          placeholder="Enter GST number"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-gst-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Project Name (Optional)</Label>
                        <Input
                          value={customer.project_name}
                          onChange={(e) => setCustomer(prev => ({ ...prev, project_name: e.target.value }))}
                          placeholder="Enter project name"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-project-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Delivery Location</Label>
                        <Input
                          value={customer.delivery_location}
                          onChange={(e) => setCustomer(prev => ({ ...prev, delivery_location: e.target.value }))}
                          placeholder="Enter delivery location"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-delivery-input"
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ─── Referrer ─── */}
                <AccordionItem value="referrer" className="border-heda-line">
                  <AccordionTrigger
                    className="py-3 text-sm font-bold uppercase tracking-wide text-heda-ink hover:no-underline"
                    data-testid="accordion-referrer-trigger"
                  >
                    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 pr-2">
                      Referrer
                      <span className="text-[9px] font-medium normal-case tracking-normal bg-heda-mute/15 text-heda-sub px-1.5 py-0.5 rounded-lg">
                        Internal — hidden from PDF
                      </span>
                      {referrerSummary && (
                        <span
                          className="basis-full truncate text-[11px] font-semibold normal-case tracking-normal text-heda-gold"
                          title={referrerSummary}
                          data-testid="referrer-summary"
                        >
                          {referrerSummary}
                        </span>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Carpenter Name</Label>
                        <Input
                          value={customer.carpenter_name || ''}
                          onChange={(e) => setCustomer(prev => ({ ...prev, carpenter_name: e.target.value }))}
                          placeholder="Optional"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-carpenter-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Architect Name</Label>
                        <Input
                          value={customer.architect_name || ''}
                          onChange={(e) => setCustomer(prev => ({ ...prev, architect_name: e.target.value }))}
                          placeholder="Optional"
                          className="rounded-xl mt-1 bg-heda-cream/50 border-heda-line text-sm"
                          data-testid="customer-architect-input"
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ─── T&C (per-quotation override) ─── */}
                <AccordionItem value="terms" className="border-b-0 border-heda-line">
                  <AccordionTrigger
                    className="py-3 text-sm font-bold uppercase tracking-wide text-heda-ink hover:no-underline"
                    data-testid="accordion-terms-trigger"
                  >
                    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pr-2">
                      <FileText className="w-3.5 h-3.5" /> T&amp;C
                      {hasCustomTerms ? (
                        <span
                          className="text-[9px] font-semibold normal-case tracking-normal bg-heda-gold-tint text-heda-gold px-1.5 py-0.5 rounded-lg"
                          data-testid="terms-custom-badge"
                        >
                          Custom terms
                        </span>
                      ) : (
                        <span className="text-[9px] font-medium normal-case tracking-normal bg-heda-olive-tint text-heda-olive-dark px-1.5 py-0.5 rounded-lg">
                          This quotation only
                        </span>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div className="flex items-center justify-end mb-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await api.getSettings();
                            const terms = (res.data?.terms || []).slice();
                            setDefaultTerms(terms);
                            setTermsOverride(terms.slice());
                            toast.success('Reset to default T&C');
                          } catch { toast.error('Failed to reset'); }
                        }}
                        className="text-[10px] text-heda-mute hover:text-heda-sub"
                        data-testid="reset-terms-btn"
                      >
                        Reset to default
                      </button>
                    </div>
                    <div className="bg-heda-cream border border-heda-line rounded-xl p-3 space-y-1.5" data-testid="terms-editor">
                      {termsList.map((t, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[10px] text-heda-mute w-5 shrink-0 font-mono">{i + 1}]</span>
                          <MarkdownInput
                            value={t}
                            onChange={(val) => {
                              const next = [...termsList];
                              next[i] = val;
                              setTermsOverride(next);
                            }}
                            className="bg-white border-heda-line text-xs h-8 flex-1"
                            testId={`cart-term-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() => setTermsOverride(termsList.filter((_, idx) => idx !== i))}
                            className="text-red-700 hover:bg-red-50 rounded-lg p-1"
                            aria-label="Remove"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setTermsOverride([...termsList, ''])}
                        className="text-xs text-heda-olive-dark hover:underline flex items-center gap-1 mt-1"
                      >
                        <Plus className="w-3 h-3" /> Add another term
                      </button>
                      <p className="text-[10px] text-heda-mute pt-1 border-t border-heda-line/60">
                        Edits only affect this quotation's PDF. Use <code className="bg-white px-1 rounded-lg">**bold**</code> / <code className="bg-white px-1 rounded-lg">*italic*</code> or B / I buttons. Admin → PDF / T&amp;C tab edits the company default.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              </div>

              {/* lg:sticky bottom-0: before the page has scrolled far enough
                  for the card to reach its sticky spot, the card can still
                  reach past the bottom of the screen; the buttons then
                  stick to the bottom edge instead of being cut off. */}
              <div className="mt-3 border-t border-heda-line pt-4 lg:sticky lg:bottom-0 lg:z-10 lg:shrink-0 lg:bg-white lg:pb-3" data-testid="quotation-actions">

              {/* Comparison Mode: Show Comparison PDF button */}
              {isComparisonMode && (
                <div className="flex flex-col gap-2 mb-2">
                  <Button
                    onClick={handleSaveChanges}
                    disabled={saving || generating || generatingComparison || items.length === 0}
                    variant="outline"
                    className="rounded-xl w-full whitespace-normal leading-tight text-center bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-11 text-xs sm:text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                    data-testid="save-changes-btn"
                  >
                    {saving ? 'Saving...' : <><Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Save Option 2</>}
                  </Button>
                  <Button
                    onClick={handleGenerateComparisonPdf}
                    disabled={generating || saving || generatingComparison || items.length === 0}
                    className="rounded-xl w-full whitespace-normal leading-tight text-center bg-heda-gold hover:bg-heda-gold/90 text-white uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-12 text-xs sm:text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                    data-testid="generate-comparison-pdf-btn"
                  >
                    {generatingComparison ? 'Generating...' : <><FileStack className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Download Comparison PDF</>}
                  </Button>
                  <p className="text-xs text-heda-mute text-center">This will generate a PDF with Option 1, Option 2, and a comparison table.</p>
                </div>
              )}

              {/* Edit Mode (non-comparison): Show both Save and Download buttons */}
              {isEditMode && !isComparisonMode && !quotationIsComparison && (
                <>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <Button
                      onClick={handleSaveChanges}
                      disabled={saving || generating || items.length === 0}
                      variant="outline"
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-11 text-xs sm:text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      data-testid="save-changes-btn"
                    >
                      {saving ? 'Saving...' : <><Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Save</>}
                    </Button>
                    <Button
                      onClick={() => handleGenerate(false)}
                      disabled={generating || saving || items.length === 0}
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-heda-ink hover:bg-black text-white uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-11 text-xs sm:text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      data-testid="generate-quotation-btn"
                    >
                      {generating ? 'Processing...' : <><FileDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Save & Download</>}
                    </Button>
                  </div>
                </>
              )}
              
              {/* Edit Mode for existing comparison quotation (Option 1 or Option 2) */}
              {isEditMode && !isComparisonMode && quotationIsComparison && (
                <div className="flex flex-col gap-2 mb-2">
                  {/* The primary CTA when editing a comparison
                      is downloading the COMPARISON PDF — matching how it
                      was originally saved. Single-option PDF is still
                      available below as a secondary. This addresses the
                      user complaint: "when I edit + download it comes as
                      a single PDF, not the comparison". */}
                  <Button
                    onClick={handleGenerateComparisonPdf}
                    disabled={generating || saving || generatingComparison || items.length === 0}
                    className="rounded-xl w-full whitespace-normal leading-tight text-center bg-heda-olive hover:bg-heda-olive-dark text-white uppercase tracking-wider font-semibold h-auto py-2 min-h-11 sm:min-h-12 text-sm shadow-heda disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="generate-comparison-pdf-btn"
                  >
                    {generatingComparison ? 'Generating…' : <><FileStack className="w-4 h-4 mr-2" /> Save &amp; Download Comparison PDF</>}
                  </Button>
                  <p className="text-[10px] sm:text-xs text-heda-mute text-center -mt-1">
                    Contains BOTH options + a comparison summary. This is the format the customer originally received.
                  </p>
                  {/* Secondary: single-option or save-only. */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleSaveChanges}
                      disabled={saving || generating || generatingComparison || items.length === 0}
                      variant="outline"
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase tracking-wider font-semibold h-auto py-2 min-h-9 sm:min-h-10 text-[11px] sm:text-xs disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      data-testid="save-changes-btn"
                    >
                      {saving ? 'Saving...' : <><Save className="w-3 h-3 mr-1" /> Save {quotationComparisonLabel} only</>}
                    </Button>
                    <Button
                      onClick={() => handleGenerate(false)}
                      disabled={generating || saving || generatingComparison || items.length === 0}
                      variant="outline"
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-white border-heda-line text-heda-sub hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase tracking-wider font-semibold h-auto py-2 min-h-9 sm:min-h-10 text-[11px] sm:text-xs disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      data-testid="generate-quotation-btn"
                    >
                      {generating ? 'Processing...' : <><FileDown className="w-3 h-3 mr-1" /> Only {quotationComparisonLabel}</>}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Create Mode: Two Generate buttons */}
              {!isEditMode && (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleSaveQuotation}
                      disabled={saving || generating || downloadingInternal || items.length === 0}
                      variant="outline"
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-12 text-xs sm:text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      data-testid="save-quotation-btn"
                      title="Save without generating a PDF"
                    >
                      {saving ? 'Saving…' : <><Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Save Quotation</>}
                    </Button>
                    <Button
                      onClick={() => handleGenerate(false)}
                      disabled={generating || downloadingInternal || items.length === 0}
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-heda-olive hover:bg-heda-olive-dark text-white shadow-heda uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-12 text-xs sm:text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="generate-quotation-btn"
                    >
                      {generating ? 'Processing...' : <><FileDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Customer PDF</>}
                    </Button>
                    <Button
                      onClick={() => handleGenerate(true)}
                      disabled={generating || downloadingInternal || items.length === 0}
                      variant="outline"
                      className="rounded-xl flex-[1_1_9.5rem] whitespace-normal leading-tight text-center bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase tracking-wider font-semibold h-auto py-2 min-h-10 sm:min-h-12 text-xs sm:text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      data-testid="generate-internal-btn"
                      title="Internal PDF with brand names"
                    >
                      {downloadingInternal ? 'Processing...' : <><FileDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Internal PDF</>}
                    </Button>
                  </div>
                </>
              )}
              <p className="text-xs text-heda-mute text-center mt-2">{isEditMode ? 'Save saves without download, Save & Download includes PDF' : 'Save persists the quotation; PDF buttons save + download. Clicking a PDF after Save updates the same record.'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Add Dialogs */}
      <QuickAddVeneerDialog 
        open={showQuickVeneer}
        onOpenChange={setShowQuickVeneer}
        data={quickVeneerData}
        setData={setQuickVeneerData}
        onAdd={handleQuickAddVeneer}
      />
      
      <QuickAddLaminateDialog 
        open={showLaminateQuickAdd}
        onOpenChange={setShowLaminateQuickAdd}
        data={laminateQuickAddData}
        setData={setLaminateQuickAddData}
        onAdd={handleLaminateQuickAdd}
      />
      
      <QuickAddHardwareDialog 
        open={showHardwareQuickAdd}
        onOpenChange={setShowHardwareQuickAdd}
        data={hardwareQuickAddData}
        setData={setHardwareQuickAddData}
        onAdd={handleHardwareQuickAdd}
      />
      
      <QuickAddAdhesiveDialog 
        open={showAdhesiveQuickAdd}
        onOpenChange={setShowAdhesiveQuickAdd}
        data={adhesiveQuickAddData}
        setData={setAdhesiveQuickAddData}
        onAdd={handleAdhesiveQuickAdd}
      />

      <ReplaceProductDialog
        open={showReplaceDialog}
        onOpenChange={(o) => { setShowReplaceDialog(o); if (!o) setReplaceTargetItem(null); }}
        originalItem={replaceTargetItem}
        onReplace={handleQuickReplace}
        onBrowseAll={navigateToFullPicker}
      />

      {/* Duplicate-prompt modal — surfaces near-identical recent
          quotations BEFORE the save actually persists. */}
      <DuplicateReviewDialog
        open={dupDialogOpen}
        onClose={handleDupCancel}
        matches={dupMatches}
        pendingPayload={dupPendingPayload}
        onReplace={handleDupReplace}
        onKeepBoth={handleDupKeepBoth}
      />
    </div>
  );
}
